import type { RefObject } from 'react';

const GROUP_TRANSITION_MS = 300;

/**
 * Finds the nearest scrollable parent element
 * @param element - The element to start searching from
 * @returns The nearest scrollable parent or null
 */
export const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = getComputedStyle(parent);
    if (/(auto|scroll)/.test(overflow + overflowY)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

/**
 * Finds the nearest collapsed group ancestor of an element
 * @param element - The element to start searching from
 * @returns The collapsed group container or null
 */
export const getCollapsedGroupAncestor = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    if (parent.dataset.groupCollapsed === 'true') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

/**
 * Expands all collapsed groups that contain validation errors.
 * Clicks the toggle button on each collapsed group to trigger the expand animation.
 * @param formRef - Reference to the form container element
 * @returns The number of groups that were expanded
 */
export const expandCollapsedGroupsWithErrors = (
  formRef: RefObject<HTMLDivElement | null>
): number => {
  if (!formRef.current) return 0;

  const errorElements = formRef.current.querySelectorAll('[class*="validation-error"]');
  const expandedGroups = new Set<HTMLElement>();

  errorElements.forEach((errorEl) => {
    if (!(errorEl instanceof HTMLElement)) return;

    const collapsedGroup = getCollapsedGroupAncestor(errorEl);
    if (collapsedGroup && !expandedGroups.has(collapsedGroup)) {
      expandedGroups.add(collapsedGroup);

      // Find the toggle button within this group and click it to expand
      const toggleId = collapsedGroup.querySelector('[data-testid^="group-toggle-"]');
      if (toggleId instanceof HTMLElement) {
        toggleId.click();
      }
    }
  });

  return expandedGroups.size;
};

/**
 * Scrolls to the first field with a validation error (in DOM order).
 * If the error field is inside a collapsed group, the group is expanded first.
 * @param formRef - Reference to the form container element
 */
export const scrollToFirstError = (formRef: RefObject<HTMLDivElement | null>): void => {
  if (!formRef.current) return;

  // Use setTimeout to ensure validation styles have been applied
  setTimeout(() => {
    // Expand any collapsed groups that contain errors
    const expandedCount = expandCollapsedGroupsWithErrors(formRef);

    // Wait for expand animation to fully settle before scrolling.
    // GroupControl uses 300ms CSS transition + 50ms fallback overflow timer.
    const scrollDelay = expandedCount > 0 ? GROUP_TRANSITION_MS + 200 : 0;

    setTimeout(() => {
      // Find elements with validation-error class (applied by Input/TextArea/etc components)
      const errorWrapper = formRef.current?.querySelector('[class*="validation-error"]');

      if (errorWrapper instanceof HTMLElement) {
        // Find visible input inside (skip hidden inputs used by dropdowns)
        const inputElement = errorWrapper.querySelector(
          'input:not([type="hidden"]), textarea, select'
        );
        // Use the wrapper itself for scrolling (it's always visible), input for focus
        const scrollParent = getScrollParent(errorWrapper);

        if (scrollParent) {
          const elementRect = errorWrapper.getBoundingClientRect();
          const parentRect = scrollParent.getBoundingClientRect();
          const scrollTop =
            scrollParent.scrollTop + elementRect.top - parentRect.top - parentRect.height / 2;
          scrollParent.scrollTo({ top: scrollTop, behavior: 'smooth' });
        } else {
          errorWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Focus the visible input after scroll settles
        if (inputElement instanceof HTMLElement) {
          setTimeout(() => {
            inputElement.focus({ preventScroll: true });
          }, 300);
        }
      }
    }, scrollDelay);
  }, 100);
};
